import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Fish, Target, Anchor, MessageCircle, ShoppingBag, Users, Shield } from "lucide-react";
import { AmericanFlag } from "@/components/AmericanFlag";

const HERO_IMG = "https://images.unsplash.com/photo-1605499907240-d4240d766581";
const CAT_IMAGES = [
  { url: "https://images.unsplash.com/photo-1515631604561-23e0be68ee06", label: "Saltwater", id: "saltwater", icon: Fish },
  { url: "https://images.pexels.com/photos/6204497/pexels-photo-6204497.jpeg", label: "Hunting", id: "hunting", icon: Target },
  { url: "https://images.unsplash.com/photo-1625183656263-171183307b15", label: "Boating", id: "boating", icon: Anchor },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div data-testid="landing-page" className="animate-fade-in">
      <div className="patriot-band" aria-hidden />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-6 lg:col-span-6">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-5">
              <span className="w-8 h-px bg-accent" />
              For Florida Sportsmen, by Sportsmen
            </div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              The salt, the swamp, the shot.
              <span className="block text-primary mt-2">One community.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
              Swap stories from the flats, argue over the best inshore rig, buy and sell gear with folks who actually
              know a Penn from a Shimano. Florida Sportsman is the forum and marketplace built for the state's hunters,
              anglers, and boaters.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-6"
                onClick={() => navigate(user ? "/forum" : "/login")}
                data-testid="btn-hero-cta"
              >
                {user ? "Enter the Forum" : "Join the community"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-12 px-6 border-primary/30 hover:bg-primary/5"
                onClick={() => navigate("/marketplace")}
                data-testid="btn-hero-marketplace"
              >
                Browse marketplace
              </Button>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <Stat number="6" label="Forum categories" />
              <Stat number="7" label="Gear categories" />
              <Stat number="FL" label="Locals only" />
            </div>
          </div>

          <div className="md:col-span-6 lg:col-span-6">
            <div className="relative rounded-md overflow-hidden shadow-[0_20px_60px_rgba(27,77,62,0.18)] aspect-[4/5] md:aspect-[5/6]">
              <img src={HERO_IMG} alt="Florida sportsman at sunset" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-primary-foreground">
                <div className="text-xs uppercase tracking-widest opacity-80">Trip log · June</div>
                <div className="font-display font-bold text-2xl mt-1">"Popped the school just past sunrise off Boca Grande."</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="border-y border-border bg-muted/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          <Feature icon={Users} title="Forum built for locals"
            body="Post reports, ask for tide advice, brag about the catch. Six categories cover salt, fresh, hunt, boat, gear and general." />
          <Feature icon={ShoppingBag} title="Marketplace for real gear"
            body="Sell that old Penn Spinfisher, list your used tree stand, or find a bay boat trailer. Direct-to-buyer with secure Stripe checkout." />
          <Feature icon={MessageCircle} title="Talk before you trade"
            body="Every listing has a built-in message thread. Ask questions, haggle, arrange pickup — all inside the platform." />
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent mb-2">Explore the forum</div>
            <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Pick your patch of Florida</h2>
          </div>
          <Link to="/forum" className="hidden md:inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline" data-testid="link-all-categories">
            All categories <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {CAT_IMAGES.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.id}
                to={`/forum?category=${c.id}`}
                className="group relative rounded-md overflow-hidden aspect-[4/5] block"
                data-testid={`category-tile-${c.id}`}
              >
                <img src={c.url} alt={c.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/10 to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-primary-foreground flex items-center justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest opacity-80">Category</div>
                    <div className="font-display font-bold text-2xl mt-1">{c.label}</div>
                  </div>
                  <Icon className="w-6 h-6" />
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display font-bold text-3xl md:text-4xl">Ready to swap stories or sell that reel?</h2>
            <p className="mt-4 text-primary-foreground/80 max-w-lg">
              Sign in with Google in one click. No forums to configure, no accounts to remember. Start posting or listing in seconds.
            </p>
          </div>
          <div className="flex md:justify-end">
            <Button
              size="lg"
              className="h-12 px-8 bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              onClick={() => navigate(user ? "/forum" : "/login")}
              data-testid="btn-cta-signin"
            >
              {user ? "Go to Forum" : "Sign in with Google"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* 2A / NRA supporter banner */}
      <section className="bg-patriot-blue text-white relative overflow-hidden" data-testid="section-2a-supporter">
        <div className="patriot-band" aria-hidden />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-center relative">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 mb-3">
              <Shield className="w-3.5 h-3.5" /> Proud American, proud Floridian
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight">
              Firm supporters of the <span style={{ color: "hsl(355 68% 68%)" }}>Second Amendment</span> and the <span style={{ color: "hsl(355 68% 68%)" }}>NRA</span>.
            </h2>
            <p className="mt-4 text-white/80 max-w-2xl leading-relaxed">
              We believe in the right to keep and bear arms — and we believe in doing it lawfully. Florida Sportsman
              is a listing platform where sportsmen buy, sell, and trade firearms in full compliance with federal,
              Florida, and local law. Buyer and seller assume all responsibility for each transaction.
            </p>
            <div className="mt-6">
              <Button
                size="lg"
                onClick={() => navigate("/subscribe")}
                className="h-12 px-6 bg-white hover:bg-white/90"
                style={{ color: "hsl(218 62% 24%)" }}
                data-testid="btn-firearms-pass-cta"
              >
                <Shield className="w-4 h-4 mr-2" /> Get the Firearms Pass
              </Button>
            </div>
          </div>
          <div className="md:col-span-5 flex md:justify-end" data-testid="landing-flag">
            <div className="w-full max-w-md rounded-md overflow-hidden ring-1 ring-white/10">
              <AmericanFlag className="w-full h-auto block" waving ariaHidden={false} />
            </div>
          </div>
        </div>
        <div className="patriot-band" aria-hidden />
      </section>
    </div>
  );
}

const Stat = ({ number, label }) => (
  <div>
    <div className="font-display font-bold text-3xl text-primary">{number}</div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
  </div>
);

const Feature = ({ icon: Icon, title, body }) => (
  <div className="flex gap-4">
    <div className="w-11 h-11 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h3 className="font-display font-bold text-lg text-foreground">{title}</h3>
      <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  </div>
);
